import React, { useState, useEffect, useCallback } from 'react';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';
import { boardService, type TaskBoard } from '@/lib/boardService';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useWorkspace } from '@/context/WorkspaceContext';
import { toast } from 'sonner';

interface ProjectTaskTabProps {
  projectId: string;
  projectName: string;
  taskBoardId: string | null;
}

export const ProjectTaskTab: React.FC<ProjectTaskTabProps> = ({
  taskBoardId,
}) => {
  const { currentWorkspace } = useWorkspace();
  const [board, setBoard] = useState<TaskBoard | null>(null);
  const [loading, setLoading] = useState(true);

  const loadBoard = useCallback(async () => {
    if (!taskBoardId || !currentWorkspace) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await boardService.getBoardById(taskBoardId);
      setBoard(data);
    } catch (err) {
      console.error('Error loading project board:', err);
      toast.error('Failed to load task board');
    } finally {
      setLoading(false);
    }
  }, [taskBoardId, currentWorkspace]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const handleHeaderUpdate = useCallback(async (updates: {
    title?: string;
    description?: string;
    background?: any;
    slug?: string;
  }) => {
    if (!board) return;
    try {
      const updated = await boardService.updateBoard(board.id, updates);
      setBoard(updated);
      toast.success('Board updated');
    } catch (err) {
      console.error('Error updating board:', err);
      toast.error('Failed to update board');
      throw err;
    }
  }, [board]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (!board) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-muted-foreground">No task board found for this project.</p>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-10 py-6 w-full min-w-0">
      <KanbanBoard
        board={board}
        headerConfig={{
          title: board.title,
          description: board.description,
          background: board.background,
          hideBanner: board.hide_banner,
          enableUpvotes: board.enable_upvotes ?? (board.background as any)?.enableUpvotes ?? false,
        }}
        onHeaderUpdate={handleHeaderUpdate}
        currentSlug={board.slug}
      />
    </div>
  );
};

export default ProjectTaskTab;
